<template>
  <div class="product-meta">
    <h1 class="product-meta__title" itemprop="name">{{ product.product.name }} {{ product.sort.name }}</h1>

    <div class="product-meta__rating-and-badges">
      <div class="product-meta__rating">
        <i class="product-meta__rating-star icon icon-star-rating" />
        <span>5.0</span>
      </div>

      <div v-if="isSpecialOffer" class="product-meta__badge">
        <svg
          width="14"
          height="14"
          fill="currentColor"
          xmlns="http://www.w3.org/2000/svg"
          class="product-card__badge-icon"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="m2.17178 11.4108-.80902-.4806c-.35825-.2127-.57989-.5967-.585092-1.01333L.766056 8.976C.763553 8.7761.711001 8.58.613201 8.40572L.15293 7.5851c-.2039068-.36349-.2039068-.80672 0-1.1702l.460271-.82062c.0978-.17428.150354-.37038.152855-.57029l.011612-.94086c.005205-.41663.226832-.80061.585092-1.01333l.80902-.4806c.17187-.1021.31533-.24555.41742-.41742l.4806-.80902c.21271-.35825.5967-.57989 1.01333-.585092l.94086-.011612c.19991-.002503.396-.055055.57029-.152855L6.4149.15293c.36349-.2039068.80671-.2039068 1.1702 0l.82062.460271c.17428.0978.37038.150354.57029.152855l.94086.011612c.41663.005205.80063.226832 1.01333.585092l.4806.80902c.1021.17187.2455.31533.4174.41742l.809.4806c.3583.21271.5799.5967.5851 1.01333l.0116.94086c.0025.19991.0551.396.1529.57029l.4603.82062c.2039.36348.2039.80671 0 1.1702l-.4603.82062c-.0978.17428-.1504.37038-.1529.57028l-.0116.94087c-.0052.41663-.2268.80063-.5851 1.01333l-.809.4806c-.1719.1021-.3153.2455-.4174.4174l-.4806.809c-.2127.3583-.5967.5799-1.01333.5851l-.94086.0116c-.19991.0025-.396.0551-.57029.1529l-.82062.4603c-.36349.2039-.80671.2039-1.1702 0l-.82062-.4603c-.17428-.0978-.37038-.1504-.57029-.1529l-.94086-.0116c-.41663-.0052-.80061-.2268-1.01333-.5851l-.4806-.809c-.1021-.1719-.24555-.3153-.41742-.4174Zm2.44809-2.50486c-.21221.25296-.17928.63005.07368.84236.25305.21232.63014.17928.84245-.07368l3.84341-4.58067c.21222-.25295.17929-.63004-.07377-.84235-.25296-.21232-.63005-.17929-.84236.07367L4.61987 8.90594ZM5.7313 5.73163c.46699-.46708.46699-1.22423 0-1.69122-.46709-.46709-1.22424-.46709-1.69132 0-.46699.46699-.46699 1.22416 0 1.69122.46708.46699 1.22423.46699 1.69132 0Zm4.22806 4.22806c.46704-.46699.46704-1.22416 0-1.69122-.46699-.46699-1.22416-.46699-1.69123 0-.46698.46708-.46698 1.22423 0 1.69122.46709.46711 1.22424.46711 1.69123 0Z"
          />
        </svg>

        <span>{{ $t('Special offer') }}</span>
      </div>
    </div>

    <div class="product-meta__date-and-views">
      <span class="product-meta__date">
        {{ localeDate(product.updated_at, true) }}
      </span>

      <span class="product-meta__num-views">
        <i class="icon icon-eye" />
        <span>{{ product.num_views }}</span>
      </span>
    </div>
  </div>
</template>

<script>
import localeDate from '@/helpers/filters/localeDate'
import { Star, Eye } from 'lucide-vue'

export default {
  name: 'ProductMeta',
  components: {
    Star,
    Eye,
  },
  props: {
    product: { type: Object, required: true },
  },
  computed: {
    isSpecialOffer() {
      return this.product.sort_weight > 0 || this.product.discount > 0
    },
  },
  methods: {
    localeDate,
  },
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/common-imports.scss';

.product-meta {
  --base-font-size: 12px;
  --base-line-height: 1.17;

  &__title {
    font-weight: 700;
    font-size: 18px;
    line-height: 1.29;

    @include media-breakpoint-up(xl) {
      font-size: 24px;
    }
  }

  &__rating-and-badges {
    display: flex;
    align-items: center;
    margin-bottom: 8px;

    @include media-breakpoint-up(xl) {
      margin-bottom: 12px;
    }
  }

  &__rating {
    display: flex;
    align-items: center;
    font-size: var(--base-font-size);
    line-height: var(--base-line-height);
    font-weight: 600;
    margin-right: 16px;
    color: $DGray;

    @include media-breakpoint-up(xl) {
      margin-right: 24px;
    }

    &-star {
      color: $Yellow;
      margin-right: 4px;
    }
  }

  &__badge {
    font-weight: 400;
    font-size: var(--base-font-size);
    line-height: var(--base-line-height);
    padding: 2px 4px;
    border-radius: 4px;
    background-color: $White;
    display: grid;
    align-items: center;
    gap: 4px;
    grid-template-columns: auto 1fr;
    border: 1px solid $LGray6;
    color: $Dark100;

    > svg {
      width: 14px;
      height: 14px;

      path {
        fill: $PlainRed;
      }
    }

    @include media-breakpoint-up(xl) {
      padding: 2px 5px;
      font-size: 14px;
    }
  }

  &__date-and-views {
    font-size: var(--base-font-size);
    line-height: var(--base-line-height);
    color: $Gray2;
    display: flex;
    align-items: center;
  }

  &__date {
    margin-right: 12px;

    @include media-breakpoint-up(xl) {
      margin-right: 18px;
    }
  }

  &__num-views {
    display: flex;
    align-items: center;

    > i {
      margin-right: 4px;
      font-size: var(--base-font-size);

      @include media-breakpoint-up(xl) {
        margin-right: 8px;
      }
    }
  }
}
</style>
